import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Tooltip,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

const REPORTS = [
  { id: 'aged-receivables-summary', label: 'Aged Receivables Summary', icon: AssessmentIcon, description: 'Overview of outstanding balances by age', path: '/billing/reports/aged-receivables-summary', comingSoon: false },
  { id: 'aged-receivables-detail', label: 'Aged Receivables Detail', icon: TimelineIcon, description: 'Detailed breakdown of receivables aging', path: '/billing/reports/aged-receivables-detail', comingSoon: false },
  { id: 'dunning', label: 'Dunning Dashboard', icon: WarningIcon, description: 'Overdue invoices by aging bucket with reminder controls', path: '/billing/reports/dunning', comingSoon: false },
  { id: 'profitability', label: 'Profitability', icon: TrendingUpIcon, description: 'Revenue vs. costs and profitability metrics', path: null, comingSoon: true },
  { id: 'revenue-by-client', label: 'Revenue by Client', icon: PeopleIcon, description: 'Revenue breakdown by client', path: null, comingSoon: true },
  { id: 'revenue-by-service-item', label: 'Revenue by Service Item', icon: CategoryIcon, description: 'Revenue by service or product', path: null, comingSoon: true },
  { id: 'revenue-by-team-member', label: 'Revenue by Team Member', icon: PersonIcon, description: 'Revenue attributed to each team member', path: null, comingSoon: true },
  { id: 'utilization-by-team-member', label: 'Utilization by Team Member', icon: WorkIcon, description: 'Team utilization and capacity metrics', path: null, comingSoon: true },
];

const BillingReports = () => {
  const navigate = useNavigate();
  const { userPermissions } = useContext(AuthContext) || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userPermissions?.can_access_billing_settings) {
      setError('No access to billing features.');
      setLoading(false);
      return;
    }
    setLoading(false);
  }, [userPermissions]);

  const handleReportSelect = (report) => {
    if (!userPermissions?.can_access_billing_settings) {
      alert('No access to billing features.');
      return;
    }
    if (report.comingSoon) return;
    if (report.path) navigate(report.path);
  };

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'primary.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AssessmentIcon sx={{ color: 'primary.main', fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Billing Reports</Typography>
          <Typography variant="body2" color="text.secondary">Generate aged receivables, profitability, and revenue reports</Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {REPORTS.map((report) => {
          const Icon = report.icon;
          const card = (
            <Card
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: '4px solid',
                borderLeftColor: report.comingSoon ? 'grey.400' : 'primary.main',
                transition: 'box-shadow 0.2s ease',
                opacity: report.comingSoon ? 0.7 : 1,
                '&:hover': { boxShadow: report.comingSoon ? 0 : 2 },
              }}
            >
              <CardActionArea onClick={() => handleReportSelect(report)} disabled={report.comingSoon} sx={{ p: 0 }}>
                  <CardContent sx={{ py: 2.5, px: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          bgcolor: 'primary.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon sx={{ fontSize: 24, color: 'primary.main' }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
                          {report.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {report.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
              </CardActionArea>
            </Card>
          );
          return (
            <Grid item xs={12} sm={6} md={4} key={report.id}>
              {report.comingSoon ? (
                <Tooltip title="Coming soon">
                  <span>{card}</span>
                </Tooltip>
              ) : (
                card
              )}
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default BillingReports;
